import { CaretLeft } from "@phosphor-icons/react"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Switch } from "@tokenterminal/core/ui/input/switch/Switch"
import { Divider } from "@tokenterminal/core/ui/layout/divider/Divider"
import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import {
  EXPERIMENTAL_FLAGS,
  useExperimentalFlags,
} from "hooks/useExperimentalFlags"
import { useRouter } from "next/router"
import ActionItem from "./ActionItem"

type ExperimentalFeatureSwitcherProps = {
  handleBack: () => void
}

type FeatureToggleProps = {
  label: string
  description?: string
  badgeText?: string
  isSelected: boolean
  flagKey: string
}

const FeatureToggle = ({
  label,
  description,
  badgeText,
  isSelected,
  flagKey,
}: FeatureToggleProps) => {
  const router = useRouter()
  const [, setExperimentalFlags] = useExperimentalFlags()

  const handleToggle = () => {
    setExperimentalFlags({
      [flagKey]: !isSelected,
    }).then(() => {
      setTimeout(() => {
        router.reload()
      }, 100)
    })
  }

  return (
    <ActionItem
      item={{
        label: (
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-col justify-start items-start gap-0 max-w-full w-full">
              <div className="flex justify-start items-center mb-1">
                <Badge
                  size="xsmall"
                  color={badgeText === "Beta" ? "cyan" : undefined}
                  variant="muted"
                  className="leading-normal"
                >
                  {badgeText}
                </Badge>
              </div>
              {label}
              {description && (
                <div className="text-xs text-fg-secondary">{description}</div>
              )}
            </div>
            <Switch
              isSelected={isSelected}
              onChange={handleToggle}
              className="cursor-pointer"
            />
          </div>
        ),
        onClick: handleToggle,
        style: {
          padding: "8px 10px",
        },
      }}
    />
  )
}

const ExperimentalFeatureSwitcher = ({
  handleBack,
}: ExperimentalFeatureSwitcherProps) => {
  const [experimentalFlags] = useExperimentalFlags()

  const features: {
    label: string
    description?: string
    badgeText?: string
    flagKey: string
    isSelected: boolean
  }[] = [
    {
      label: "New product page",
      badgeText: "Beta" as const,
      flagKey: EXPERIMENTAL_FLAGS.PRODUCT_PAGES_V1,
      isSelected: !!experimentalFlags[EXPERIMENTAL_FLAGS.PRODUCT_PAGES_V1],
    },
    {
      label: "Redesigned homepage",
      description:
        "Including prominent search, macro-level stats, favorites, and studio",
      badgeText: "Beta" as const,
      flagKey: EXPERIMENTAL_FLAGS.REDESIGNED_HOMEPAGE,
      isSelected: !!experimentalFlags[EXPERIMENTAL_FLAGS.REDESIGNED_HOMEPAGE],
    },
  ]

  return (
    <div className="flex flex-col p-2 gap-2">
      <div className="flex flex-col gap-2">
        <Button
          onPress={handleBack}
          variant="ghost"
          size="small"
          className="self-start !text-xs !p-2 !rounded-[6px]"
        >
          <Icon as={CaretLeft} />
          Back
        </Button>
        <div className="flex flex-col items-start pl-2">
          <div className="text-sm font-medium">Experimental features</div>
          <div className="text-xs text-fg-secondary">
            Opt-in to product experiments. Note, there may be bugs.
          </div>
        </div>
      </div>
      <Divider variant="secondary" className="-mx-2" />
      <div className="flex flex-col gap-0 with-overflow">
        {features.map((feature) => (
          <FeatureToggle
            key={feature.flagKey}
            label={feature.label}
            description={feature.description}
            badgeText={feature.badgeText}
            isSelected={feature.isSelected}
            flagKey={feature.flagKey}
          />
        ))}
      </div>
    </div>
  )
}

export default ExperimentalFeatureSwitcher
